import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

// localStorage flag so the banner only shows for first-time visitors. We
// versioned the key so a future policy change can re-trigger consent if
// needed (just bump v1 → v2).
const CONSENT_STORAGE_KEY = 'apex.cookieConsent.v1';

const hasConsented = () => {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY) === 'accepted';
  } catch {
    // If storage is blocked (privacy mode, etc.) treat as already-consented
    // so we don't pester the visitor on every page load.
    return true;
  }
};

const persistConsent = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'accepted');
  } catch {
    // Silent fail — banner just won't be hidden next time. Acceptable.
  }
};

/**
 * Apex Athletic Mind cookie consent banner.
 *
 * Minimal, non-intrusive, Apex-branded. Sits at the bottom of the viewport
 * for first-time visitors only. One "Got it" button to acknowledge plus a
 * "Privacy Policy" deep link. No GDPR consent management framework (CMF)
 * since the site doesn't run behavioural ad tech — this is a plain notice
 * banner, not a granular consent UI.
 */
export const CookieConsentBanner = () => {
  // Default to "hidden" so we never flash the banner on SSR / first paint.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hasConsented()) {
      // Tiny delay lets the page paint first so the banner slides in rather
      // than appearing simultaneously with the rest of the UI.
      const timer = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const accept = () => {
    persistConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 pointer-events-none"
    >
      <div className="mx-auto max-w-[920px] pointer-events-auto rounded-xl border border-[#004C54]/15 bg-[#0B0C0D] text-white shadow-2xl shadow-black/40 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
        <div className="flex-1 text-[12.5px] sm:text-[13px] leading-relaxed text-white/85">
          We use cookies to keep the site working and to understand how
          athletes use it. By continuing, you agree to our use of cookies.
          See our{' '}
          <a
            href="/privacy"
            className="text-[#ACC0C6] font-semibold underline hover:text-white"
            data-testid="cookie-consent-privacy-link"
          >
            Privacy Policy
          </a>{' '}
          for the details.
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={accept}
            className="inline-flex items-center justify-center rounded-full bg-[#004C54] hover:bg-[#003a40] text-white text-[12.5px] font-semibold px-4 h-9 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4FBCC7] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0C0D]"
            data-testid="cookie-consent-accept"
          >
            Got it
          </button>
          <button
            type="button"
            onClick={accept}
            aria-label="Dismiss cookie notice"
            data-testid="cookie-consent-dismiss"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
